import { ActionCreatorWithoutPayload, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  closeCheckout,
  confirmRegistration,
  confirmRegistrationEmail,
  confirmRegistrationMobile,
  createRegistration,
  finaliseAccountDetails,
  verifyRegistrationMobile,
} from '../'

import {
  CashUserMissingData,
  RegistrationData,
  RegistrationError,
  RegistrationErrorCode,
  registrationErrors,
  RegistrationStatus,
  RiskRegistrationStatus,
  VerifyRegistrationMobileRejectedActionPayload,
  WelcomeWithInitialLimitStatus,
} from '../../model'
import { PURGE } from 'redux-persist'

export interface RegistrationState {
  id?: string
  smsEnabled: boolean
  autoSendCode: boolean
  status: RegistrationStatus
  data?: RegistrationData
  error?: RegistrationError
  isManualAddress: boolean
  isUseExistingMobile: boolean
  cashUserMissingData?: CashUserMissingData
  riskRegistrationStatus: RiskRegistrationStatus
  welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus
}

export const registrationInitialState: RegistrationState = {
  status: RegistrationStatus.NotStarted,
  smsEnabled: true,
  autoSendCode: true,
  isManualAddress: false,
  isUseExistingMobile: false,
  riskRegistrationStatus: {
    emailVerificationRequired: false,
    isEmailVerified: false,
    mobileVerificationRequired: false,
  },
  welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus.None,
}

const slice = createSlice({
  name: 'registration',
  initialState: registrationInitialState,
  reducers: {
    updateRegistrationStatus(state: RegistrationState, action: PayloadAction<RegistrationStatus>): RegistrationState {
      return {
        ...state,
        status: action.payload,
      }
    },
    updateRegistrationData(state: RegistrationState, action: PayloadAction<RegistrationData>): RegistrationState {
      return {
        ...state,
        data: { ...state.data, ...action.payload },
      }
    },
    resetRegistrationState(state) {
      state.status = RegistrationStatus.NotStarted
      state.error = undefined
      state.riskRegistrationStatus = registrationInitialState.riskRegistrationStatus
    },
    toggleAddressMode(state: RegistrationState, action: PayloadAction<boolean>): RegistrationState {
      return {
        ...state,
        isManualAddress: action.payload,
      }
    },
    updateUseExistingMobile(state: RegistrationState, action: PayloadAction<boolean>): RegistrationState {
      return {
        ...state,
        isUseExistingMobile: action.payload,
      }
    },
    verifyRegistrationMobileEventError(state: RegistrationState, action: PayloadAction<VerifyRegistrationMobileRejectedActionPayload>): RegistrationState {
      const { errorCode } = action.payload
      return {
        ...state,
        status: RegistrationStatus.Error,
        error: {
          error: registrationErrors[errorCode] ?? RegistrationErrorCode.Default,
          errorCode,
        },
      }
    },
    verifyRegistrationMobileEventSuccess(state: RegistrationState, _: PayloadAction<void>): RegistrationState {
      return {
        ...state,
        status: RegistrationStatus.CodeSent,
      }
    },
    setWelcomeWithInitialLimitSeen(state: RegistrationState): RegistrationState {
      return { ...state, welcomeWithInitialLimitStatus: WelcomeWithInitialLimitStatus.Seen }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRegistration.fulfilled, (state, action) => {
        state.status = RegistrationStatus.Created
        state.id = action.payload.registrationId
        // @ts-expect-error: OPERATION BLEED STOPPER
        state.data = { ...state.data, email: action.payload.email }
        state.riskRegistrationStatus = {
          ...action.payload.registrationStatus,
          isEmailVerified: false,
        }
      })
      .addCase(verifyRegistrationMobile.fulfilled, (state, action) => {
        state.status = RegistrationStatus.CodeSent
        state.smsEnabled = action.payload.smsEnabled
      })
      .addCase(verifyRegistrationMobile.rejected, (state, action) => {
        // @ts-expect-error: OPERATION BLEED STOPPER
        const { errorCode, errorInfo } = action.payload

        state.status = RegistrationStatus.Error
        state.error = {
          error: RegistrationErrorCode.Default,
          errorCode,
          errorInfo,
        }
        if (action.meta.rejectedWithValue) {
          state.error = {
            error: registrationErrors[errorCode] ?? RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        }
      })
      .addCase(confirmRegistrationMobile.fulfilled, (state) => {
        state.status = RegistrationStatus.CodeVerified
      })
      .addCase(confirmRegistration.fulfilled, (state) => {
        state.id = undefined
      })
      .addCase(confirmRegistration.rejected, (state, action) => {
        // @ts-expect-error: OPERATION BLEED STOPPER
        const { errorCode, errorInfo } = action.payload

        state.status = RegistrationStatus.Error
        state.error = {
          error: RegistrationErrorCode.Default,
          errorCode,
          errorInfo,
        }
        if (action.meta.rejectedWithValue) {
          state.error = {
            error: registrationErrors[errorCode] ?? RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        }
      })
      .addCase(finaliseAccountDetails.rejected, (state, action) => {
        // @ts-expect-error: OPERATION BLEED STOPPER
        const { errorCode, errorInfo } = action.payload

        state.status = RegistrationStatus.Error
        state.error = {
          error: RegistrationErrorCode.Default,
          errorCode,
          errorInfo,
        }
        if (action.meta.rejectedWithValue) {
          state.error = {
            error: registrationErrors[errorCode] ?? RegistrationErrorCode.Default,
            errorCode,
            errorInfo,
          }
        }
      })
      .addCase(finaliseAccountDetails.fulfilled, (state) => {
        state.status = RegistrationStatus.Completed
      })
      .addCase(confirmRegistrationEmail.fulfilled, (state) => {
        state.riskRegistrationStatus.isEmailVerified = true
      })
      .addCase(closeCheckout, () => registrationInitialState)
      .addCase(PURGE, () => registrationInitialState)
  },
})

export const {
  updateRegistrationData,
  updateRegistrationStatus,
  toggleAddressMode,
  updateUseExistingMobile,
  verifyRegistrationMobileEventError,
  verifyRegistrationMobileEventSuccess,
  setWelcomeWithInitialLimitSeen,
} = slice.actions
// @ts-expect-error: OPERATION BLEED STOPPER
export const resetRegistrationState = slice.actions.resetRegistrationState as ActionCreatorWithoutPayload<undefined>
const { reducer } = slice
export default reducer
